import { authAttemptError } from "./authAttemptState.mjs";

function reauthenticationRequired() {
  const error = new Error("Sign in again to continue.");
  error.code = "AUTH_REAUTHENTICATION_REQUIRED";
  error.requiresReauthentication = true;
  error.privateAuthFailure = true;
  return error;
}

export function createAuthRefreshCoordinator({
  state,
  requestRefresh,
  probeCurrentUser,
  onRefreshed,
}) {
  let flight = null;
  let disposed = false;
  const assertActive = (attempt) => {
    if (disposed) throw authAttemptError();
    state.assertCurrent(attempt);
  };
  const unsubscribe = state.subscribe(() => {
    if (flight && !state.isCurrent(flight.attempt)) {
      const previous = flight;
      flight = null;
      previous.cancel(authAttemptError());
      previous.controller.abort();
    }
  });

  function refresh(requestedAttempt) {
    if (disposed) return Promise.reject(authAttemptError());
    const attempt = requestedAttempt || state.capture();
    try {
      assertActive(attempt);
    } catch (error) {
      return Promise.reject(error);
    }
    if (state.getStatus().blocked)
      return Promise.reject(authAttemptError("AUTH_LOGOUT_PENDING"));
    if (flight && state.isCurrent(flight.attempt)) return flight.promise;
    const current = { attempt, controller: new AbortController() };
    const cancelled = new Promise((_, reject) => {
      current.cancel = reject;
    });
    const operation = Promise.resolve().then(async () => {
      assertActive(attempt);
      let result;
      try {
        result = await requestRefresh(attempt, current.controller.signal);
        assertActive(attempt);
      } catch (error) {
        assertActive(attempt);
        if (error.code !== "PRODUCT_SESSION_REFRESH_CONFLICT") throw error;
        try {
          const probe = await probeCurrentUser(
            attempt,
            current.controller.signal,
          );
          assertActive(attempt);
          if (probe?.success !== true || !probe.user)
            throw reauthenticationRequired();
          result = {
            success: true,
            data: { authSession: probe.data?.authSession },
          };
        } catch (probeError) {
          assertActive(attempt);
          // A failed read is not proof that credentials are invalid.
          if (
            probeError.status === 401 ||
            probeError.requiresReauthentication
          ) {
            throw reauthenticationRequired();
          }
          throw probeError;
        }
      }
      assertActive(attempt);
      if (result?.success !== true) throw reauthenticationRequired();
      state.rememberSession(attempt, result.data?.authSession);
      const safeResult = {
        success: true,
        ...(result.tokenMetadata
          ? { tokenMetadata: result.tokenMetadata }
          : {}),
        ...(result.data?.authSession
          ? { data: { authSession: result.data.authSession } }
          : {}),
      };
      onRefreshed?.(safeResult, attempt);
      return safeResult;
    });
    current.promise = Promise.race([operation, cancelled]).finally(() => {
      if (flight === current) flight = null;
    });
    flight = current;
    return current.promise;
  }

  return {
    refresh,
    dispose() {
      disposed = true;
      unsubscribe();
      if (flight) {
        flight.cancel(authAttemptError());
        flight.controller.abort();
        flight = null;
      }
    },
  };
}
